/**
 * @file AgencyTabsPage.js
 * @description Represents the page which handles agency record tabs.
 */
import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DetailsTab from './DetailsTab';
import ThemeTab from './ThemeTab';
import LocationsTab from './LocationsTab';
import styles from './AgencyPage.module.scss';
import { getAgency } from '../../services/systemService';
import Spinner from '../ui/spinners/Spinner';
import { useAuthContext } from '../../store/AuthContext';
import { SecurityAttributes } from '../../utils/data/enums';
import { ROUTES, ROUTE_BUILDERS } from '../../constants/routes';
import { getText } from '../../i18n';

const TABS = {
    DETAILS: 'Details',
    THEME: 'Theme',
    LOCATION: 'Locations',
};

const AgencyTabsPage = () => {
    const navigate = useNavigate();
    const { agencyId } = useParams();
    const [activeTab, setActiveTab] = useState(TABS.DETAILS);
    const [agencyRecord, setAgencyRecord] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const { hasAttribute } = useAuthContext();

    const detailsTabRef = useRef();
    const themeTabRef = useRef();
    const isCreateMode = agencyId == -1;

    const getAgencyRecordId = record => {
        if (!record || typeof record !== 'object') {
            return null;
        }

        if ('id' in record && record.id !== null && record.id !== undefined) {
            return record.id;
        }

        if (
            'agencyId' in record &&
            record.agencyId !== null &&
            record.agencyId !== undefined
        ) {
            return record.agencyId;
        }

        return null;
    };

    async function fetchAgencyData() {
        try {
            setIsLoading(true);

            const data =
                agencyId != -1
                    ? await getAgency(agencyId)
                    : {
                          id: agencyId,
                          enableClientTheme: true,
                          accessoryColor: '#1366ff',
                          PageHeaderColor1: '#1366ff',
                          AccessoryTextColor: '#f8fafc',
                          LoginBackgroundColor: '#f8fafc',
                      };
            setAgencyRecord(data);
            setError(null);
        } catch (error) {
            console.error('Error retrieving agency record:', error);
            setError('Could not fetch agency record. Please try again later.');
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        if (agencyId) {
            fetchAgencyData();
        }
    }, [agencyId]);

    const returnToSearch = () => {
        navigate(ROUTES.AGENCIES);
    };

    const refreshAgencyData = createdAgency => {
        const createdAgencyId = getAgencyRecordId(createdAgency);

        if (isCreateMode && createdAgencyId) {
            navigate(ROUTE_BUILDERS.agenciesView(createdAgencyId), {
                replace: true,
            });
            return;
        }

        fetchAgencyData();
    };

    if (error) {
        return <p>{error}</p>;
    }

    const handleSave = async () => {
        if (activeTab === TABS.DETAILS) {
            await detailsTabRef.current?.handleSave();
            return;
        }

        if (activeTab === TABS.THEME && isCreateMode) {
            const detailsSaveResult = await detailsTabRef.current?.handleSave({
                suppressOnSaveCallback: true,
            });

            if (!detailsSaveResult?.success) {
                return;
            }

            const createdAgencyId = getAgencyRecordId(
                detailsSaveResult.agencyRecord,
            );

            if (!createdAgencyId) {
                return;
            }

            const themeSaveResult = await themeTabRef.current?.handleSave({
                agencyId: createdAgencyId,
                baseAgencyRecord: detailsSaveResult.agencyRecord,
                suppressOnSaveCallback: true,
            });

            if (!themeSaveResult?.success) {
                return;
            }

            navigate(ROUTE_BUILDERS.agenciesView(createdAgencyId), {
                replace: true,
            });
            return;
        }

        if (activeTab === TABS.THEME) {
            await themeTabRef.current?.handleSave();
        }
    };

    return (
        <div className={`page-content ${styles.container}`}>
            <div className="card">
                <div className="card-body px-0 py-4">
                    <div className="d-flex justify-content-between align-items-center position-relative pb-4">
                        <div className="ms-4">
                            <p className="page-title">
                                {agencyRecord?.name ?? 'Add Agency'}
                            </p>
                            <div
                                className="ing-assets-subtitle mt-1"
                                style={{
                                    minHeight: '1.0rem',
                                    visibility:
                                        activeTab === TABS.THEME
                                            ? 'visible'
                                            : 'hidden',
                                }}
                            >
                                {getText('msg_fb4f16bcb6dc')}
                                <br />
                                {getText('msg_ac226f86ccbb')}
                            </div>
                        </div>

                        <div className="d-flex align-items-center me-4">
                            {hasAttribute(
                                SecurityAttributes.AgencyFullAccess,
                            ) && (
                                <p
                                    type="button"
                                    onClick={handleSave}
                                    className="ing-text-buttons d-inline me-3"
                                    style={{ cursor: 'pointer' }}
                                >
                                    <i className="bi bi-check-lg me-2" />
                                    {getText('msg_efc007a393f6')}
                                </p>
                            )}
                            <p
                                type="button"
                                onClick={returnToSearch}
                                className="ing-text-buttons d-inline ms-3"
                            >
                                <i className="bi bi-x me-2" />
                                {getText('msg_bbfa773e5a63')}
                            </p>
                        </div>
                    </div>

                    <div className="d-flex justify-content-between align-items-center position-relative mt-4">
                        <ul className={`col-md-5 col-lg-5 ${styles.tabList}`}>
                            {Object.values(TABS).map(tab => (
                                <li
                                    key={tab}
                                    className={`${styles.tabItem} ${
                                        activeTab === tab ? styles.active : ''
                                    }`}
                                    onClick={() => setActiveTab(tab)}
                                >
                                    {tab}
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className={`w-100 pt-4`}>
                        {isLoading ? (
                            <Spinner
                                width={50}
                                className={styles.loadingSpinner}
                            />
                        ) : (
                            <div className={styles.tabContent}>
                                <div
                                    style={{
                                        display:
                                            activeTab === TABS.DETAILS
                                                ? 'block'
                                                : 'none',
                                    }}
                                >
                                    <DetailsTab
                                        ref={detailsTabRef}
                                        agencyId={agencyId}
                                        agencyRecord={agencyRecord}
                                        onSave={refreshAgencyData}
                                    />
                                </div>

                                <div
                                    style={{
                                        display:
                                            activeTab === TABS.THEME
                                                ? 'block'
                                                : 'none',
                                    }}
                                >
                                    <ThemeTab
                                        ref={themeTabRef}
                                        agencyId={agencyId}
                                        agencyRecord={agencyRecord}
                                        onSave={refreshAgencyData}
                                    />
                                </div>

                                <div
                                    style={{
                                        display:
                                            activeTab === TABS.LOCATION
                                                ? 'block'
                                                : 'none',
                                    }}
                                >
                                    <LocationsTab agencyId={agencyId} />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AgencyTabsPage;
